import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Mail, Phone } from 'lucide-react';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import { formatPhoneForDisplay } from '@/utils/phoneUtils';
import type { OrganizationInfo } from './types';

interface EventPreviewOrgSectionProps {
  organization: OrganizationInfo | null;
  hideOrganizationInfo: boolean;
  loading: boolean;
}

export function EventPreviewOrgSection({
  organization,
  hideOrganizationInfo,
  loading,
}: EventPreviewOrgSectionProps) {
  if (hideOrganizationInfo) return null;

  return (
    <Box px={{ base: '5', md: '0' }} mb="4">
      <Text as="h4" fontSize="xl" fontWeight="bold" color="volyx.text" mb="4">Organized by</Text>
      {loading ? (
        <Flex gap="3" bg="volyx.lightGray" borderRadius="xl" p="4" className="animate-pulse">
          <Box w="60px" h="60px" borderRadius="full" bg="whiteAlpha.400" flexShrink={0} />
          <Box flex="1" minW="0" css={{ '& > * + *': { marginTop: '8px' } }}>
            <Box h="4" w="50%" bg="whiteAlpha.700" borderRadius="md" />
            <Box h="3" w="75%" bg="whiteAlpha.700" borderRadius="md" />
            <Box h="3" w="66%" bg="whiteAlpha.700" borderRadius="md" />
          </Box>
        </Flex>
      ) : (
        organization && (
          <Flex gap="3" bg="volyx.lightGray" borderRadius="xl" p="4">
            <chakra.img
              src={organization.profile_image || DEFAULT_PROFILE_IMAGE}
              alt={organization.name || 'Organization'}
              w="60px"
              h="60px"
              borderRadius="full"
              flexShrink={0}
              objectFit="cover"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== DEFAULT_PROFILE_IMAGE) target.src = DEFAULT_PROFILE_IMAGE;
              }}
            />
            <Box flex="1" minW="0">
              <Text fontSize="md" fontWeight="bold" color="volyx.text" mb="0.5">
                {organization.name || 'Your Organization'}
              </Text>
              <Box css={{ '& > * + *': { marginTop: '4px' } }} mt="2">
                {organization.contact_email && (
                  <Flex align="center" gap="1.5">
                    <Box as={Mail} boxSize="14px" color="volyx.textSecondary" />
                    <Text as="span" fontSize="xs" color="volyx.textSecondary">{organization.contact_email}</Text>
                  </Flex>
                )}
                {organization.contact_phone && (
                  <Flex align="center" gap="1.5">
                    <Box as={Phone} boxSize="14px" color="volyx.textSecondary" />
                    <Text as="span" fontSize="xs" color="volyx.textSecondary">
                      {formatPhoneForDisplay(organization.contact_phone)}
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>
          </Flex>
        )
      )}
    </Box>
  );
}
